import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { useGenerationStatus } from '@hooks/apiHooks/generationsHooks';
import { usePersonaBySlug } from '@hooks/apiHooks/personasHooks';
import { useArtifacts } from '@hooks/storeHooks/useArtifacts';
import { useGenerationState } from '@hooks/storeHooks/useGenerationState';
import { useSessionFeatureState } from '@hooks/storeHooks/useSessionState';
import { useQueryClient } from '@tanstack/react-query';
/*import { OidcUser } from '@types/oidcUser';
import { getOrganizationId } from '@utils/getOrganization';
import { useAuth } from 'react-oidc-context';*/
import { getOrganizationId } from '@utils/getOrganization.ts';
import { useAuth } from 'react-oidc-context';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useSessionStorage } from 'usehooks-ts';
import { userGenerationPromptKey } from '../../../../config.ts';
import { OidcUser } from '../../../../types/oidcUser.ts';
import { DISCONNECT_AVATAR_PRESERVE_GENERATION_PAGE } from '../../constants/sessionEvents';
import { useWebRTCMessageHandler } from '../../hooks/handleWebRtcMessage';
import { useCompanionChatAnalytics } from '../../hooks/useCompanionChatAnalytics';
import { useDataChannel } from '../../hooks/useDataChannel';
import { useMicrophones } from '../../hooks/useMicrophones';
import { usePiPHandlers } from '../../hooks/usePiPHandlers';
import { useScreenShare } from '../../hooks/useScreenShare';
import { useSessionPiP } from '../../hooks/useSessionPiP';
import { useSessionWebRTC } from '../../hooks/useSessionWebRTC';
import { Controls } from '../Controls';
import IdleVideoLoop from '../IdleVideoLoop';
import Loader from '../Loader';
import VideoStream from '../VideoStream';

interface VideoContainerProps {
  isIdleVideoLoaded: boolean;
  setIsIdleVideoLoaded: React.Dispatch<React.SetStateAction<boolean>>;
}

const VideoContainer: React.FC<VideoContainerProps> = ({
  isIdleVideoLoaded,
  setIsIdleVideoLoaded,
}) => {
  const { user } = useAuth();
  const orgId = user ? getOrganizationId(user as OidcUser) : '';
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const startParam = searchParams.get('start');
  const userInput = searchParams.get('userInput');

  const audioRef = useRef<HTMLAudioElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isTouchVisible, setIsTouchVisible] = useState(false);
  const hideTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [userClosedSession, setUserClosedSession] = useState(false);

  const [userGenerationPrompt] = useSessionStorage<string | null>(
    userGenerationPromptKey,
    null,
  );
  const {
    avatarReady,
    companionSlug,
    secondsBalance,
    taskId,
    isDesignerSession,
    companionData,
    sessionId,
    isSessionPage,
    isConnecting,
    autoConnect,
    setCompanionData,
    setAvatarReady,
    resetSessionState,
    setDataChannel,
    setSessionId,
    setIsConnecting,
    setTranscriptTime,
    setAutoConnect,
    setIsFileUploading,
    socketId,
    isFileUploading,
  } = useSessionFeatureState();

  const isCapabilitySession = !!taskId || isDesignerSession;
  const isMusicGenerationSession = !!taskId;
  const capabilityType = isDesignerSession
    ? 'design'
    : isMusicGenerationSession
      ? 'music'
      : 'other';

  const { isRegeneratingMusic, isRegeneratingLyrics } = useGenerationState();

  const { data: musicGeneration } = useGenerationStatus(
    isMusicGenerationSession ? taskId || null : null,
    isMusicGenerationSession,
    isRegeneratingMusic,
    isRegeneratingLyrics,
  );

  const { setArtifactsList } = useArtifacts();

  const { data: companion } = usePersonaBySlug(companionSlug || '');

  const hdVideo = useMemo(() => {
    const hd = companion?.data?.avatar?.videos?.find(
      (video) => video.video_type === 'hd',
    );
    return hd || companion?.data?.avatar?.videos?.[0];
  }, [companion]);

  const { isOpen, onMessage, sendMessage } = useDataChannel();

  // Use the extracted WebRTC message handler hook
  const { handleWebRTCMessage } = useWebRTCMessageHandler({
    startParam,
    sendMessage,
  });

  const {
    isMuted,
    stream: userMicrophoneStream,
    selectedMicrophone,
    muteMicrophone,
    connectMicrophone,
    disconnectMicrophone,
  } = useMicrophones(false);

  const {
    videoStream,
    audioStream,
    isWebRtcConnected,
    screenShareCaptureRef,
    stopWebrtc,
    startWebrtc,
  } = useSessionWebRTC({
    avatarId: companion?.data.avatar?.id || '',
    userMicrophoneStream,
    selectedMicrophone,
    mentorId: companion?.data.mentorId || '',
    companionSlug: companion?.data.slug,
    isCapabilitySession,
    generationPrompt:
      userGenerationPrompt || musicGeneration?.prompt || userInput || undefined,
    capabilityType,
    negotiateType: 'default',
  });

  // Analytics: track only companion chat sessions in this UI
  useCompanionChatAnalytics({
    enabled: true,
    sessionId: sessionId || null,
    onMessage,
    entryPoint: 'Companions Page',
    companion: companion?.data,
    isUserClosed: userClosedSession,
  });
  const {
    isCapturing: isScreenSharing,
    toggleScreenShare,
    stopScreenShare,
    startScreenShare,
    isSupported,
  } = useScreenShare(screenShareCaptureRef.current);

  const handleConnect = async () => {
    try {
      if (isWebRtcConnected) return;
      // Prevent connection if balance is 0
      if (secondsBalance === 0) {
        console.log('Cannot connect: balance is 0');
        return;
      }
      setIsConnecting(true);
      setAutoConnect(false);

      // Pause music player before starting WebRTC session
      const audioElement = document.querySelector(
        'audio[aria-label="Music"]',
      ) as HTMLAudioElement;
      if (audioElement) {
        audioElement.pause();
      }

      // Pre-warm the companion audio element so Safari allows playback later
      // when the remote audio stream arrives (after the user gesture expires).
      // Calling play() during the gesture "activates" the element in Safari's
      // autoplay policy.  We fire-and-forget — do NOT await, because Safari
      // may never resolve play() for a data-URI source, which would block
      // connectMicrophone below indefinitely.
      const companionAudio = audioRef.current;
      if (companionAudio) {
        companionAudio.src =
          'data:audio/wav;base64,UklGRigAAABXQVZFZm10IBIAAAABAAEARKwAAIhYAQACABAAZGF0YQQAAAAAAA==';
        companionAudio.play().catch(() => {});
      }

      // Capture the mic stream directly so startWebrtc can use it immediately,
      // rather than waiting for React state to propagate.
      const micStream = await connectMicrophone();
      await startWebrtc(micStream);
    } catch (error) {
      console.error('Failed to connect:', error);
    }
  };

  const handleScreenShare = async () => {
    await toggleScreenShare();
  };

  const handleDisconnect = (preventNavigation: boolean = false) => {
    // Mark that user explicitly closed the session
    setUserClosedSession(true);

    if (isSessionPage) {
      // Remove persona cache to prevent stale data on reconnect
      if (companionSlug) {
        queryClient.removeQueries({
          queryKey: ['getPersonaBySlug', companionSlug],
        });
      }
      if (location.pathname !== '/pricing' && !preventNavigation) {
        const prevLocation = location.state?.prevLocation as string | undefined;
        const isGenerationSession = location.pathname.startsWith('/generation/');
        const fallback = isGenerationSession ? '/' : '/companions';

        if (prevLocation) {
          navigate(prevLocation);
        } else {
          navigate(fallback);
        }
      }
      resetSessionState(true);
    } else {
      // partial reset
      setIsConnecting(false);
      setAvatarReady(false);

      setDataChannel(null);
      setSessionId(undefined);
      setTranscriptTime(null);
    }
    stopWebrtc();
    stopScreenShare();
    disconnectMicrophone();
  };

  // PiP handlers for audio, microphone, and session management
  // Note: sendMessageToPiP will be set after useSessionPiP is called
  const {
    handlePiPVolumeChange,
    handlePiPMuteChange,
    handlePiPMicMuteChange,
    stopSessionFromPiP,
  } = usePiPHandlers({
    audioRef,
    isMuted,
    isWebRtcConnected,
    muteMicrophone,
    stopWebrtc,
    stopScreenShare,
    handleDisconnect,
  });

  const handleTrackUploadEvent = useCallback(
    (event: string) => {
      setIsFileUploading(event === 'upload_started');
    },
    [setIsFileUploading],
  );

  useEffect(() => {
    sendMessageToPiP({
      type: 'fileUploadStateChanged',
      data: { isFileUploading: isFileUploading },
    });
  }, [isFileUploading]);

  // Picture-in-Picture functionality for session
  const { sendMessageToPiP } = useSessionPiP({
    organizationId: orgId,
    isWatchSession: false,
    isScreenSharing,
    hideScreenShareButton: !isSupported,
    videoElement: videoRef.current,
    isSessionActive: avatarReady && isWebRtcConnected,
    hasMediaStream: !!videoStream || !!audioStream,
    audioRef,
    initialVolume: audioRef.current?.volume ?? 1,
    initialMuted: audioRef.current?.muted ?? false,
    companionData,
    avatarReady: avatarReady,
    onVolumeChange: handlePiPVolumeChange,
    onMuteChange: handlePiPMuteChange,
    onMicMuteChange: handlePiPMicMuteChange,
    onEndSessionComplete: stopSessionFromPiP,
    isMuted,
    muteMicrophone,
    userMicrophoneStream,
    startScreenShare: startScreenShare,
    stopScreenShare: stopScreenShare,
    sessionId,
    sendMessage,
    onTrackUploadEvent: handleTrackUploadEvent,
    avatarId: companion?.data.avatar?.id || '',
    socketId: socketId || '',
    isFileUploading,
  });

  const handleGroupTouchStart = () => {
    setIsTouchVisible(true);
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
    }
  };

  const handleGroupTouchEnd = () => {
    hideTimeoutRef.current = setTimeout(() => {
      setIsTouchVisible(false);
    }, 3000);
  };

  useEffect(() => {
    sendMessageToPiP({
      type: 'shareStateChanged',
      data: { shareScreenState: isScreenSharing },
    });
  }, [isScreenSharing, sendMessageToPiP]);

  useEffect(() => {
    const unsubscribe = onMessage(handleWebRTCMessage as any);
    return () => {
      unsubscribe?.();
    };
  }, [isOpen, onMessage, handleWebRTCMessage]);

  const disconnectAvatarPreserveGenerationPage = useCallback(() => {
    if (!isSessionPage || !isCapabilitySession) return;

    setUserClosedSession(true);

    if (isOpen && sessionId) {
      sendMessage({
        type: 'cancel',
        data: { sessionId },
      });
    }

    setIsConnecting(false);
    setAvatarReady(false);
    setDataChannel(null);
    setSessionId(undefined);
    setTranscriptTime(null);
    stopWebrtc();
    stopScreenShare();
    disconnectMicrophone();
  }, [
    isSessionPage,
    isCapabilitySession,
    isOpen,
    sessionId,
    sendMessage,
    setIsConnecting,
    setAvatarReady,
    setDataChannel,
    setSessionId,
    setTranscriptTime,
    stopWebrtc,
    stopScreenShare,
    disconnectMicrophone,
  ]);

  useEffect(() => {
    const onDisconnectPreserve = () => {
      disconnectAvatarPreserveGenerationPage();
    };
    window.addEventListener(
      DISCONNECT_AVATAR_PRESERVE_GENERATION_PAGE,
      onDisconnectPreserve,
    );
    return () => {
      window.removeEventListener(
        DISCONNECT_AVATAR_PRESERVE_GENERATION_PAGE,
        onDisconnectPreserve,
      );
    };
  }, [disconnectAvatarPreserveGenerationPage]);

  useEffect(() => {
    if (companion && companionSlug !== companionData?.slug) {
      stopWebrtc();
      disconnectMicrophone();
      setCompanionData(companion.data);
      setArtifactsList(companion.data.artifacts);
    }
  }, [companion, companionSlug]);

  useEffect(() => {
    if (autoConnect && companionData && secondsBalance && secondsBalance > 0) {
      handleConnect();
    }
  }, [autoConnect, companionData, secondsBalance]);

  // Kill WebRTC connection when balance reaches 0
  useEffect(() => {
    if (secondsBalance === 0 && isWebRtcConnected) {
      console.log('Balance reached 0, disconnecting WebRTC');
      stopWebrtc();
      disconnectMicrophone();
      setAvatarReady(false);
      setIsConnecting(false);
    }
  }, [secondsBalance, isWebRtcConnected]);

  useEffect(() => {
    return () => {
      handleDisconnect(true);
    };
  }, []);

  useEffect(() => {
    return () => {
      if (hideTimeoutRef.current) {
        clearTimeout(hideTimeoutRef.current);
      }
    };
  }, []);

  // Manage companion audio playback.  The <audio> element lives here (always
  // in the DOM) rather than inside VideoStream so that handleConnect's
  // pre-warm can reach it before avatarReady is true.
  useEffect(() => {
    const audioEl = audioRef.current;
    if (!audioEl) return;

    audioEl.srcObject = audioStream || null;

    if (!audioStream) return;

    // Try to play immediately.  If the browser blocks it (Safari autoplay
    // policy), set up one-shot listeners that retry on the next user gesture.
    let listenersBound = false;

    const resume = () => {
      audioEl.play().catch(() => {});
      document.removeEventListener('click', resume);
      document.removeEventListener('touchstart', resume);
      listenersBound = false;
    };

    audioEl.play().catch(() => {
      document.addEventListener('click', resume);
      document.addEventListener('touchstart', resume);
      listenersBound = true;
    });

    return () => {
      if (listenersBound) {
        document.removeEventListener('click', resume);
        document.removeEventListener('touchstart', resume);
      }
    };
  }, [audioStream]);

  return (
    <div
      className={`group flex flex-col items-center justify-center gap-4 ${
        isIdleVideoLoaded ? 'opacity-100' : 'opacity-0 pointer-events-none'
      }`}
      onTouchStart={handleGroupTouchStart}
      onTouchEnd={handleGroupTouchEnd}>
      {/* Always in DOM so the pre-warm in handleConnect can activate it */}
      <audio ref={audioRef} className="hidden" />
      <div
        id="avatar-circle"
        className="relative w-40 h-40 rounded-full z-[3] shadow-lg bg-black shadow-[rgba(0,0,0,0.5)] transition-opacity duration-300 overflow-hidden">
        <div className="absolute inset-0 rounded-full pointer-events-none bg-gradient-to-b from-white/10 to-transparent" />
        <Loader
          isConnecting={isConnecting}
          isMuted={isMuted}
          isConnected={isWebRtcConnected}
        />
        {!avatarReady ? (
          <IdleVideoLoop
            setIdleVideoLoaded={setIsIdleVideoLoaded}
            hdVideo={hdVideo}
          />
        ) : (
          <VideoStream
            hdVideo={hdVideo}
            videoStream={videoStream}
            videoRef={videoRef}
          />
        )}
      </div>
      <Controls
        isMuted={isMuted}
        companion={companion?.data}
        audioRef={audioRef}
        isConnected={isWebRtcConnected || isConnecting}
        isScreenSharing={isScreenSharing}
        showScreenShareButton={isSupported}
        setMuted={muteMicrophone}
        handleConnect={handleConnect}
        handleDisconnect={handleDisconnect}
        handleReset={() => {
          resetSessionState(true);
        }}
        onScreenShare={handleScreenShare}
        isTouchVisible={isTouchVisible}
        secondsBalance={secondsBalance}
      />
    </div>
  );
};

export default VideoContainer;
